import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface FilterDropdownProps<T> {
  label: string;
  /** Static options from field config; merged with values derived from `data`. */
  options?: string[];
  selectedValues: string[];
  onFilterChange: (values: string[]) => void;
  placeholder?: string;
  getValue: (item: T) => string | undefined;
  data: T[];
}

function FilterDropdown<T>({
  label,
  options: optionsFromConfig = [],
  selectedValues,
  onFilterChange,
  placeholder = 'All',
  getValue,
  data,
}: FilterDropdownProps<T>) {
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);

  // Get unique values from data
  const uniqueValues = useMemo(() => {
    const values = new Set<string>();
    data.forEach((item) => {
      const value = getValue(item);
      if (value) {
        values.add(value);
      }
    });
    return Array.from(values).sort();
  }, [data, getValue]);

  // Add special "(Empty)" option if any items have empty/null values
  const hasEmptyValues = useMemo(() => {
    return data.some((item) => {
      const value = getValue(item);
      return !value || value.trim() === '';
    });
  }, [data, getValue]);

  // Merge config/static options (e.g. status presets) with values from the current dataset
  const allOptions = useMemo(() => {
    const merged = new Set<string>([...optionsFromConfig.filter(Boolean), ...uniqueValues]);
    const list = Array.from(merged);
    list.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    if (hasEmptyValues) {
      return ['(Empty)', ...list];
    }
    return list;
  }, [hasEmptyValues, optionsFromConfig, uniqueValues]);

  // Filter options based on search text
  const filteredOptions = useMemo(() => {
    if (!searchText) return allOptions;
    return allOptions.filter((option) =>
      option.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [allOptions, searchText]);

  // Update dropdown position when open and on scroll/resize
  useEffect(() => {
    if (!isOpen || !buttonRef.current || !dropdownMenuRef.current) return;

    const updatePosition = () => {
      if (buttonRef.current && dropdownMenuRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const dropdownMaxHeight = 400; // Increased to show more items (was 256px)
        
        // Calculate available space below and above the button
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        
        // Determine if we should show dropdown above or below
        const showAbove = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;
        
        // Calculate position
        let top: number;
        if (showAbove) {
          // Position above the button
          top = buttonRect.top - dropdownMaxHeight - 4;
          // Ensure it doesn't go above viewport
          if (top < 0) {
            top = 4;
          }
        } else {
          // Position below the button
          top = buttonRect.bottom + 4;
          // Ensure it doesn't go below viewport
          if (top + dropdownMaxHeight > viewportHeight) {
            top = viewportHeight - dropdownMaxHeight - 4;
          }
        }
        
        // Calculate left position (ensure it doesn't go off-screen)
        let left = buttonRect.left;
        if (left + buttonRect.width > viewportWidth) {
          left = viewportWidth - buttonRect.width;
        }
        if (left < 0) {
          left = 4;
        }
        
        dropdownMenuRef.current.style.top = `${top}px`;
        dropdownMenuRef.current.style.left = `${left}px`;
        dropdownMenuRef.current.style.width = `${buttonRect.width}px`;
        dropdownMenuRef.current.style.maxHeight = `${Math.min(dropdownMaxHeight, showAbove ? spaceAbove - 8 : spaceBelow - 8)}px`;
      }
    };

    // Initial position update
    updatePosition();

    // Update position on scroll and resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check if click is outside both the button container and the dropdown menu
      const isOutsideButton = dropdownRef.current && !dropdownRef.current.contains(target);
      const isOutsideMenu = dropdownMenuRef.current && !dropdownMenuRef.current.contains(target);
      
      // Only close if click is outside both
      if (isOutsideButton && isOutsideMenu) {
        setIsOpen(false);
        setSearchText('');
      }
    };

    if (isOpen) {
      // Use click event instead of mousedown to allow checkbox onChange to fire first
      document.addEventListener('click', handleClickOutside, true);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isOpen]);

  const handleToggle = (value: string) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onFilterChange(newValues);
  };

  const handleSelectAll = () => {
    if (selectedValues.length === filteredOptions.length) {
      onFilterChange([]);
    } else {
      onFilterChange([...filteredOptions]);
    }
  };

  // Helper to check if a value is an exclusion (starts with "!")
  const isExclusion = (value: string): boolean => {
    return value.startsWith('!');
  };

  // Helper to get the actual value from an exclusion
  const getExclusionValue = (value: string): string => {
    return value.startsWith('!') ? value.substring(1) : value;
  };

  const handleClear = () => {
    onFilterChange([]);
    setSearchText('');
  };

  const handleTextInput = (text: string) => {
    if (text.trim() === '') {
      onFilterChange([]);
    } else {
      // Apply text as filter - this will filter records that contain this text
      onFilterChange([text]);
    }
  };

  const displayText = useMemo(() => {
    if (selectedValues.length === 0) {
      return placeholder;
    }
    
    // Separate inclusions and exclusions for display
    const inclusions = selectedValues.filter(v => !v.startsWith('!'));
    const exclusions = selectedValues.filter(v => v.startsWith('!')).map(v => v.substring(1));
    
    const parts: string[] = [];
    
    if (inclusions.length > 0) {
      if (inclusions.length === 1 && !allOptions.includes(inclusions[0])) {
        // Single text filter (not from list)
        parts.push(inclusions[0]);
      } else if (inclusions.length === 1) {
        parts.push(inclusions[0]);
      } else {
        parts.push(`${inclusions.length} selected`);
      }
    }
    
    if (exclusions.length > 0) {
      if (exclusions.length === 1) {
        parts.push(`≠ ${exclusions[0]}`);
      } else {
        parts.push(`≠ ${exclusions.length} excluded`);
      }
    }
    
    return parts.join(', ') || placeholder;
  }, [selectedValues, allOptions, placeholder]);

  return (
    <div className="relative" ref={dropdownRef}>
      <label className={`block text-xs font-medium ${colors.textSecondary} mb-2`}>{label}</label>
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full px-3 py-2 text-sm text-left ${colors.surface} border ${colors.border} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 flex items-center justify-between`}
        >
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span className={selectedValues.length === 0 ? colors.textMuted : colors.text}>
              {displayText}
            </span>
          </div>
          <svg
            className={`w-4 h-4 ${colors.textMuted} transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div 
            ref={dropdownMenuRef}
            data-filter-label={label}
            className="fixed z-50 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden"
          >
            {/* Filter Header */}
            <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span>Filter Options</span>
              </div>
            </div>

            {/* Search Input */}
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                value={searchText}
                onChange={(e) => {
                  const text = e.target.value;
                  setSearchText(text);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchText.trim()) {
                    handleTextInput(searchText);
                    setIsOpen(false);
                    setSearchText('');
                  }
                }}
                placeholder="Type to search or filter..."
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
              <p className={`text-xs ${colors.textMuted} mt-1`}>Type and press Enter to filter by text</p>
            </div>

            {/* Select All / Clear */}
            <div className={`flex items-center justify-between px-3 py-2 border-b ${colors.border} ${colors.surfaceHover}`}>
              <button
                type="button"
                onClick={handleSelectAll}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
              >
                {selectedValues.length === filteredOptions.length ? 'Deselect All' : 'Select All'}
              </button>
              {selectedValues.length > 0 && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-xs text-red-600 hover:text-red-700 font-medium"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Options List */}
            <div className="overflow-y-auto" style={{ maxHeight: '320px' }}>
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500 text-center">No options found</div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = selectedValues.includes(option);
                  const isExcluded = selectedValues.includes(`!${option}`);
                  const displayOption = option === '(Empty)' ? '(Empty)' : option;
                  
                  return (
                    <div key={option} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50">
                      <label className="flex items-center cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleToggle(option);
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <span className={`ml-2 text-sm ${colors.textSecondary}`}>{displayOption}</span>
                        {isSelected && (
                          <svg className="w-4 h-4 text-blue-600 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </label>
                      {option !== '(Empty)' && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const exclusionValue = `!${option}`;
                            if (isExcluded) {
                              // Remove exclusion
                              onFilterChange(selectedValues.filter(v => v !== exclusionValue));
                            } else {
                              // Add exclusion
                              onFilterChange([...selectedValues.filter(v => v !== option), exclusionValue]);
                            }
                          }}
                          className={`ml-2 px-2 py-1 text-xs rounded ${
                            isExcluded 
                              ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          title={isExcluded ? 'Remove exclusion' : 'Exclude this value'}
                        >
                          {isExcluded ? '≠' : '≠'}
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FilterDropdown;

